import { useMemo } from 'react';
import styled from '@emotion/styled';
import { useSetAtom } from 'jotai/react';
import { useTranslation } from 'next-i18next';
import IcChevronRight from 'src/assets/images/icChevronRightRegular.svg';
import ResizingText from 'src/components/ResizingText';
import SubscriptionModal from 'src/components/VividiOnly/Subscription';
import { useHasInventoryItem, useItemRemainingSeconds } from 'src/hooks/useInventoryItem';
import { closeModalAtom, showModalAtom } from 'src/stores/modal/atoms';
import { ModalType } from 'src/types/Modal';
import { hexToRgb } from 'src/utils/common';
import { DAY_S, HOUR_S, MINUTE_S } from 'src/utils/time';
const SubscriptionIcon = styled.img`
  width: 24px;
  height: 24px;
  object-fit: scale-down;
`;
const SubscriptionIconArrow = styled(IcChevronRight)`
  color: ${({
  theme
}) => theme.color.content6};
  width: 24px;
  height: 24px;
`;
const SubscriptionRowTitle = styled(ResizingText)`
  font-size: 16px;
  font-weight: 700;
`;
const SubscriptionRowDesc = styled(ResizingText)`
  font-size: 12px;
  font-weight: 400;
`;
const SubscriptionRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-grow: 1;
`;
const Container = styled.button<{
  hasSubscription: boolean;
}>`
  all: unset;
  cursor: pointer;

  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;

  width: 100%;
  box-sizing: border-box;
  padding: 16px;
  border-radius: 16px;

  ${({
  hasSubscription,
  theme
}) => hasSubscription ? `
        border: 1px solid ${theme.color.surface4};

        ${SubscriptionRowTitle} {
          color: ${theme.color.content11};
        }
        ${SubscriptionRowDesc} {
          color: ${theme.color.content9};
        }
      ` : `
        background-color: ${theme.color.surface12};
        box-shadow: 0px 2px 20px 0px rgba(${hexToRgb(theme.color.surfaceDeepFixed)}, 0.13);

        ${SubscriptionRowTitle} {
          color: ${theme.color.content2};
        }

        ${SubscriptionRowDesc} {
          color: ${theme.color.content9};
        }
  `}
`;
export default function SubscriptionCard(): JSX.Element {
  const {
    t
  } = useTranslation();
  const showModal = useSetAtom(showModalAtom);
  const closeModal = useSetAtom(closeModalAtom);
  const hasSubscription = useHasInventoryItem('VIVIDI_UNLIMITED');
  const remainingSeconds = useItemRemainingSeconds('VIVIDI_UNLIMITED');
  const description = useMemo(() => {
    if (!hasSubscription || remainingSeconds == null || remainingSeconds <= 0) {
      return t('vividi_profile_notsubs_status_desc');
    }
    if (remainingSeconds >= DAY_S) {
      const days = Math.floor(remainingSeconds / DAY_S);
      return t('vividi_profile_subs_status_left_days', {
        days
      });
    } else if (remainingSeconds >= HOUR_S) {
      const hours = Math.floor(remainingSeconds / HOUR_S);
      return t('vividi_profile_subs_status_left_hours', {
        hours
      });
    } else {
      const min = Math.floor(remainingSeconds / MINUTE_S);
      return t('vividi_profile_subs_status_left_mins', {
        min
      });
    }
  }, [t, hasSubscription, remainingSeconds]);
  const handleClick = () => {
    closeModal(ModalType.HEADER_POPUP);
    showModal({
      key: ModalType.SUBSCRIPTION_POPUP,
      component: () => <SubscriptionModal referrer='my_profile' />
    });
  };
  return <Container hasSubscription={hasSubscription} onClick={handleClick} data-sentry-element="Container" data-sentry-component="SubscriptionCard" data-sentry-source-file="SubscriptionCard.tsx">
      <SubscriptionIcon src='/vividi/images/icons/icUnlimited.png' alt={t('vividi_profile_section_mysub_title')} data-sentry-element="SubscriptionIcon" data-sentry-source-file="SubscriptionCard.tsx" />
      <SubscriptionRow data-sentry-element="SubscriptionRow" data-sentry-source-file="SubscriptionCard.tsx">
        <SubscriptionRowTitle minFontSize={16} maxLines={2} data-sentry-element="SubscriptionRowTitle" data-sentry-source-file="SubscriptionCard.tsx">
          {hasSubscription ? t('vividi_profile_section_mysub_title') : t('vividi_profile_notsubs_status_title')}
        </SubscriptionRowTitle>
        <SubscriptionRowDesc minFontSize={12} maxLines={2} data-sentry-element="SubscriptionRowDesc" data-sentry-source-file="SubscriptionCard.tsx">
          {description}
        </SubscriptionRowDesc>
      </SubscriptionRow>
      <SubscriptionIconArrow data-sentry-element="SubscriptionIconArrow" data-sentry-source-file="SubscriptionCard.tsx" />
    </Container>;
}